import { SimpleDocCommand } from '../../apps/DocsApp/commands/v5/types';
import { StandardToolResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import fetchService from '../../services/fetchService';
import { API_BASE_URL } from '../../api/config';

export const code_execute: SimpleDocCommand = {
  id: 'code_execute',
  logicalName: 'code_execute',
  version: 'v2',
  description: `Execute code for operations impractical with existing tools: calculations, bulk operations, data analysis, format conversions.

**WHEN TO USE:**
- Complex calculations, statistics, data transformations
- Bulk/repetitive operations (e.g., apply formatting to 500 pages)
- File generation (charts, images, exports, custom formats)
- Format conversions (CSV→JSON, image processing, etc.)
- Operations requiring libraries (pandas, numpy, matplotlib)

**WHEN NOT TO USE:**
- Creating River entities (sheets, docs, slides, canvas, notes) → Use entity_create
- Content updates or CRUD operations → Use entity_write

**SUPPORTED LANGUAGES:**
- Python (pandas, numpy, matplotlib, requests, beautifulsoup, etc.)
- JavaScript/Node.js
- Bash/Shell

**FILE PERSISTENCE:**
Any file you write to disk in your code will be saved to the workspace as an entity:
- Python: with open('output.txt', 'w') as f: f.write('data')
- JavaScript: fs.writeFileSync('output.json', JSON.stringify(data))
- Bash: echo "data" > output.txt

Files that will NOT be saved:
- Input files (from input_entities parameter) - these are provided for reading only
- System files (river_tools.py) - automatically excluded
- river_manifest.json - processed separately for entity operations

To save your script itself as a file, write it explicitly:
with open('my_script.py', 'w') as f: f.write('# your code here')

**FILE NAMING:**
Use descriptive, lowercase names with underscores:
- Good: sales_analysis.py, revenue_chart.png, quarterly_report.csv
- Avoid: temp.py, output.txt, file1.json (generic, unclear purpose)
- Reserved names (will NOT be saved): river_tools.py, river_manifest.json, input file names

**INPUT ENTITIES:**
Process existing workspace entities:
- Pass entities via input_entities parameter
- JSON format (recommended): Preserves ALL structure (formulas, formatting, metadata)
- CSV/Markdown format: Simplified for basic operations (lossy conversion)
- Entities appear as files in sandbox for processing
- Use river_tools library to update entities after processing

**EXAMPLES:**

Data analysis:
{
  "language": "python",
  "input_entities": [{"entity_id": "sheet_sales", "format": "csv"}],
  "code": "import pandas as pd\\ndf=pd.read_csv('sales.csv')\\nprint(f'Total: {df[\\"Revenue\\"].sum()}')"
}

Generate chart:
{
  "language": "python",
  "input_entities": [{"entity_id": "sheet_revenue"}],
  "code": "import pandas as pd, matplotlib.pyplot as plt\\ndf=pd.read_csv('revenue.csv')\\nplt.plot(df['Month'], df['Revenue'])\\nplt.savefig('chart.png')\\nfrom river_tools import RiverEntity\\nRiverEntity.create_image('Revenue Chart', 'chart.png')"
}

**RIVER TOOLS LIBRARY:**
Helper library for entity operations (auto-injected in sandbox):
- RiverEntity.create_image(name, file) - Create image from generated file
- RiverEntity.update_sheet_json(id, file) - Update preserving formulas/formatting
- RiverEntity.load_json(file) - Load entity JSON
- RiverEntity.save_json(data, file) - Save entity JSON

**LIMITS:**
- Execution timeout: 30 seconds
- Max file size: 10MB per file
- Code runs in isolated sandbox`,

  schema: {
    parameters: [
      {
        name: 'language',
        type: 'string',
        description: 'Programming language to execute',
        required: true,
        enum: ['python', 'javascript', 'bash'],
        validator: (value: string) => {
          const validLanguages = ['python', 'javascript', 'bash'];
          if (!validLanguages.includes(value)) {
            return `Language must be one of: ${validLanguages.join(', ')}`;
          }
          return null;
        },
      },
      {
        name: 'code',
        type: 'string',
        description: 'Code to execute. Any file you write to disk (e.g., open("file.txt", "w")) will be saved to the workspace. Input files and system files are excluded.',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Code cannot be empty';
          if (value.length > 50000) return 'Code exceeds maximum length of 50,000 characters';
          return null;
        }
      },
      {
        name: 'workspace_folder',
        type: 'string',
        description: 'Optional folder ID (group_*) where generated files should be saved.',
        required: false,
        validator: (value: string) => {
          if (value && !value.startsWith('group_')) return 'Workspace folder must be a valid folder ID (group_*)';
          return null;
        }
      },
      {
        name: 'input_entities',
        type: 'array',
        description: 'Array of workspace entities to make available as input files. Supports JSON (native), CSV, or Markdown formats.',
        required: false,
        validator: (value: any) => {
          if (!Array.isArray(value)) return 'input_entities must be an array';
          if (value.length > 20) return 'Maximum 20 input entities allowed';
          for (const item of value) {
            if (typeof item !== 'object' || item === null || !item.entity_id) return 'Each input entity must be an object with entity_id';
            if (item.format && !['json', 'csv', 'markdown'].includes(item.format)) return 'Format must be json, csv, or markdown';
            if (item.alias) {
              if (typeof item.alias !== 'string') return 'Invalid alias - must be a string';
              if (!/^[a-zA-Z0-9_.-]+$/.test(item.alias)) return 'Invalid alias - only alphanumeric, underscore, dash, and dot allowed';
              if (item.alias.length === 0 || item.alias.length > 255) return 'Invalid alias - must be between 1 and 255 characters';
            }
          }
          return null;
        }
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { language, code, workspace_folder, input_entities } = params;

    const normalizedInputEntities = (input_entities || []).map((entity: any) => ({
      ...entity,
      format: entity.format || 'json'
    }));

    try {
      const result = await fetchService(`${API_BASE_URL}/code-execution`, {
        method: 'POST',
        body: JSON.stringify({
          language,
          code,
          workspace_folder,
          space_id: context.spaceId,
          input_entities: normalizedInputEntities
        })
      });

      if (result.error || result.detail) {
        return {
          status: 'error',
          message: 'Code execution failed',
          error: { code: 'execution_error', category: 'execution', details: result.detail || result.error || 'Unknown error occurred' },
          format: 'plaintext'
        };
      }

      const createdEntities = result.entities_created || result.files_created || [];
      const updatedEntities = result.entities_updated || [];
      const failedOperations = result.entities_failed || result.files_failed || [];

      const entitiesCreatedMsg = createdEntities.length > 0 ? `\n\n**Created:** ${createdEntities.length}\n${createdEntities.map((f: any) => `- ${f.name} (${f.entity_type})`).join('\n')}` : '';
      const entitiesUpdatedMsg = updatedEntities.length > 0 ? `\n\n**Updated:** ${updatedEntities.length}\n${updatedEntities.map((f: any) => `- ${f.name} (${f.entity_type})`).join('\n')}` : '';
      const entitiesFailedMsg = failedOperations.length > 0 ? `\n\n**Failed:** ${failedOperations.length}\n${failedOperations.map((f: any) => `- ${f.name || f.operation?.name || 'Unknown'}: ${f.error || 'Unknown error'}`).join('\n')}` : '';

      const responseData: StandardToolResult = {
        status: 'success' as const,
        message: `Code executed successfully.${entitiesCreatedMsg}${entitiesUpdatedMsg}${entitiesFailedMsg}`,
        data: {
          language,
          stdout: result.stdout,
          stderr: result.stderr,
          exit_code: result.exit_code,
          entities_created: createdEntities.map((f: any) => ({ entity_id: f.entity_id || f.llm_id || 'unknown', name: f.name || 'Unnamed Entity', entity_type: f.entity_type || 'Unknown', llm_id: f.llm_id || f.entity_id || 'unknown' })),
          entities_updated: updatedEntities.map((f: any) => ({ entity_id: f.entity_id || f.llm_id || 'unknown', name: f.name || 'Unnamed Entity', entity_type: f.entity_type || 'Unknown', llm_id: f.llm_id || f.entity_id || 'unknown' })),
          entities_failed: failedOperations,
          stats: { execution_time_ms: result.execution_time_ms || 0, entities_created_count: createdEntities.length, entities_updated_count: updatedEntities.length, entities_failed_count: failedOperations.length, output_size_bytes: (result.stdout?.length || 0) + (result.stderr?.length || 0) }
        },
        format: 'json'
      };

      const allEntityIds = [
        ...createdEntities.map((e: any) => e.entity_id || e.llm_id),
        ...updatedEntities.map((e: any) => e.entity_id || e.llm_id)
      ].filter(Boolean);

      if (allEntityIds.length > 0) {
        try {
          const { entityService } = await import('../../services/entityService');
          const entities = await entityService.queryEntities({ ids: allEntityIds.join(',') });

          if (entities.length > 0) {
            const { default: useEntityStore } = await import('../../stores/entityStore');
            const currentEntities = useEntityStore.getState().entities;
            const getEntityKey = (entity: any): string | null => entity.llmId || entity._id?.toString() || entity.id || null;
            const entityMap = new Map();
            for (const e of currentEntities) { const key = getEntityKey(e); if (key) entityMap.set(key, e); }
            for (const entity of entities) { const key = getEntityKey(entity); if (key) entityMap.set(key, entity); }
            useEntityStore.getState().setEntities(Array.from(entityMap.values()));
          }
        } catch {
          // Don't fail the command — files are saved, user can manually refresh
        }
      }

      return responseData;

    } catch (error: any) {
      return {
        status: 'error',
        message: 'Failed to execute code',
        error: { code: 'execution_error', category: 'execution', details: error.message || 'Unknown error occurred' },
        format: 'plaintext'
      };
    }
  }
};
